import { useTranslation } from 'react-i18next';
import Container from '@/components/ui/Container';
import Button from '@/components/ui/Button';
import LanguageSelector from '@/components/ui/LanguageSelector';
import { QrCodeIcon } from '@/components/icons';

export default function Hero() {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-screen flex items-center bg-gradient-to-br from-mk-pink-500 via-mk-pink-600 to-mk-pink-700 overflow-hidden">
      {/* Header bar */}
      <header className="absolute top-0 left-0 right-0 z-20 bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a href="/" className="text-xl font-bold">
              <span className="text-white">MyPink</span>
              <span className="text-mk-pink-400">Contact</span>
            </a>

            {/* Right side */}
            <div className="flex items-center gap-4">
              <LanguageSelector variant="light" />
              <a
                href="/login"
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
              >
                {t('nav.signIn')}
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -right-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-1/4 -left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
      </div>

      <Container className="relative z-10 pt-28 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Text content */}
          <div className="text-center lg:text-left">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fadeInUp">
              {t('hero.title')}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 animate-fadeInUp-delay-1">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fadeInUp-delay-2">
              <Button href="/login" variant="white">
                {t('hero.cta')}
              </Button>
              <Button href="#how-it-works" variant="outline-white">
                {t('hero.seeHow')}
              </Button>
            </div>
          </div>

          {/* Phone mockup */}
          <div className="flex justify-center lg:justify-end">
            <div className="animate-float">
              <PhoneMockup />
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

function PhoneMockup() {
  const { t } = useTranslation();

  return (
    <div className="relative w-64 md:w-72">
      {/* Phone frame */}
      <div className="bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
        {/* Screen */}
        <div className="bg-white rounded-[2.5rem] overflow-hidden">
          {/* Status bar */}
          <div className="h-6 bg-gray-100 flex items-center justify-center">
            <div className="w-20 h-4 bg-gray-900 rounded-full" />
          </div>

          {/* Profile card preview */}
          <div className="p-4">
            {/* Accent bar */}
            <div className="h-8 bg-gradient-to-r from-mk-pink-500 to-mk-pink-600 rounded-t-lg" />

            {/* Card content */}
            <div className="bg-white shadow-lg rounded-b-lg p-4 -mt-4 pt-8 text-center">
              {/* Avatar */}
              <div className="w-16 h-16 mx-auto -mt-12 mb-3 rounded-full bg-gradient-to-br from-mk-pink-400 to-mk-pink-600 flex items-center justify-center text-white text-xl font-bold border-4 border-white shadow-lg">
                JD
              </div>

              <h3 className="font-bold text-gray-900 text-sm">Jane Doe</h3>
              <p className="text-gray-500 text-xs mb-3">{t('mockup.beautyConsultant')}</p>

              {/* QR placeholder */}
              <div className="w-24 h-24 mx-auto bg-gray-100 rounded-lg flex items-center justify-center mb-3">
                <QrCodeIcon className="w-16 h-16 text-gray-400" />
              </div>

              {/* Buttons */}
              <div className="flex gap-2 justify-center">
                <div className="px-3 py-1.5 bg-mk-pink-600 text-white text-xs rounded-full">
                  {t('mockup.contact')}
                </div>
                <div className="px-3 py-1.5 border border-mk-pink-600 text-mk-pink-600 text-xs rounded-full">
                  {t('mockup.save')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute -top-4 -right-4 w-20 h-20 bg-white/20 rounded-full blur-xl" />
      <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-white/20 rounded-full blur-xl" />
    </div>
  );
}
